import { Link, createFileRoute } from '@tanstack/react-router';
import { ListChecks, Plus } from 'lucide-react';

import { Button } from '@/components/Button';
import { PageHeader } from '@/components/PageHeader';
import { ExerciseSearch } from '@/components/exercise/ExerciseSearch';
import { exerciseFiltersSchema, type ExerciseFilters } from '@/components/exercise/filters';

/**
 * Catálogo de ejercicios.
 *
 * Los filtros viven en la URL: así se puede compartir una búsqueda, y volver
 * atrás desde una ficha devuelve la lista tal y como estaba.
 */
export const Route = createFileRoute('/_app/exercises/')({
  validateSearch: (search: Record<string, unknown>): ExerciseFilters =>
    exerciseFiltersSchema.parse(search),
  component: ExercisesPage,
});

function ExercisesPage() {
  const filters = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <>
      <PageHeader
        title="Ejercicios"
        subtitle="Busca en el catálogo o crea los tuyos."
        action={
          <Link
            to="/exercises/new"
            aria-label="Nuevo ejercicio"
            className="flex size-11 items-center justify-center rounded-xl bg-brand text-brand-ink"
          >
            <Plus className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      {/* Desde `xs` (y en escritorio) los resultados en rejilla de tarjetas. Se
          aplica desde fuera porque `ExerciseSearch` también vive en el
          selector de ejercicios, que es estrecho y debe seguir en una columna. */}
      <div className="xs:[&>div>ul]:grid xs:[&>div>ul]:grid-cols-2 xl:[&>div>ul]:grid-cols-3">
        <ExerciseSearch
          value={filters}
          onChange={(next) => {
            // `replace` para que teclear no llene el historial de pasos atrás.
            void navigate({ search: next, replace: true });
          }}
          emptyAction={
            <Button
              variant="secondary"
              icon={<Plus className="size-4" />}
              onClick={() => void navigate({ to: '/exercises/new' })}
            >
              Crear un ejercicio
            </Button>
          }
        />
      </div>

      <div className="mt-6 mb-2">
        <Link
          to="/routines"
          search={{ archived: false }}
          className="flex min-h-14 items-center gap-3 rounded-2xl border border-line bg-surface-raised px-4 text-ink-muted transition-colors hover:bg-surface-overlay"
        >
          <ListChecks className="size-5 text-brand" aria-hidden="true" />
          <span className="text-sm font-medium text-ink">Ir a mis rutinas</span>
        </Link>
      </div>
    </>
  );
}
